<!--
  Component: DateInput.vue
  Mô tả: Component nhập liệu ngày tháng tiện dụng, hỗ trợ cả gõ phím DD/MM/YYYY (có mask),
  lịch chọn trực quan (v-date-picker locale vi) và phím tắt chọn nhanh 1-chạm (Hôm nay, Đầu tháng, Hiện tại/01-01-2100, Gợi ý, Thai sản).
-->
<template>
  <div class="date-input-container">
    <v-menu
      ref="dateMenu"
      v-model="menu"
      :close-on-content-click="false"
      transition="scale-transition"
      offset-y
      min-width="290px"
      max-width="310px"
      :disabled="disabled || readonly"
    >
      <template v-slot:activator="{ on, attrs }">
        <v-text-field
          :value="displayValue"
          @input="onTextInput"
          @change="onTextChange"
          @blur="$emit('blur', $event)"
          :label="label"
          :placeholder="placeholder"
          :rules="rules"
          :dense="dense"
          :outlined="outlined"
          :flat="flat"
          :solo="solo"
          :disabled="disabled"
          :readonly="readonly"
          :clearable="clearable && !readonly && !disabled"
          @click:clear="clearDate"
          :hide-details="hideDetails"
          v-mask="'##/##/####'"
          persistent-placeholder
          v-bind="attrs"
          class="rounded-lg"
          append-icon="mdi-calendar"
          @click:append="openMenu"
          autocomplete="off"
        >
          <template v-slot:append-outer>
            <slot name="append-outer"></slot>
          </template>
        </v-text-field>
      </template>

      <!-- Menu nội dung lịch picker -->
      <v-card class="rounded-lg overflow-hidden elevation-4" style="border: 1px solid #e0e0e0;">
        <!-- Thanh chọn nhanh trong popup -->
        <div class="pa-2 grey lighten-4 d-flex align-center justify-space-between" style="border-bottom: 1px solid #e0e0e0;">
          <span class="text-caption font-weight-bold grey--text text--darken-3">Chọn nhanh:</span>
          <div class="d-flex align-center gap-1">
            <v-btn x-small text color="teal darken-3" class="font-weight-bold px-1" @click="setToday">
              Hôm nay
            </v-btn>
            <v-btn x-small text color="teal darken-3" class="font-weight-bold px-1" @click="setFirstDayOfMonth">
              Đầu tháng
            </v-btn>
            <v-btn v-if="isEndDate" x-small text color="primary darken-2" class="font-weight-bold px-1" @click="setMaxDate">
              <v-icon x-small left>mdi-infinity</v-icon> Hiện tại
            </v-btn>
          </div>
        </div>

        <!-- Date picker Vuetify 2 -->
        <v-date-picker
          v-model="pickerDate"
          locale="vi"
          color="teal darken-3"
          no-title
          scrollable
          @input="onPickerSelect"
        ></v-date-picker>

        <v-card-actions class="pt-0 px-3 pb-2 justify-end grey lighten-5">
          <v-btn text small color="grey darken-2" @click="menu = false">Đóng</v-btn>
        </v-card-actions>
      </v-card>
    </v-menu>

    <!-- Các chip chọn nhanh 1-chạm đặt ngoài giao diện (giúp thao tác cực nhanh không cần mở popup) -->
    <div
      v-if="quickPresets && !disabled && !readonly"
      class="d-flex flex-wrap align-center mt-1"
      style="gap: 4px;"
    >
      <v-chip
        x-small
        outlined
        color="teal darken-3"
        class="font-weight-medium px-2"
        style="cursor: pointer; height: 22px;"
        @click="setToday"
      >
        <v-icon x-small left>mdi-calendar-today</v-icon> Hôm nay
      </v-chip>

      <v-chip
        x-small
        outlined
        color="grey darken-2"
        class="font-weight-medium px-2"
        style="cursor: pointer; height: 22px;"
        @click="setFirstDayOfMonth"
      >
        Đầu tháng
      </v-chip>

      <v-chip
        v-if="isEndDate"
        x-small
        color="blue lighten-5"
        text-color="primary darken-3"
        class="font-weight-bold px-2"
        style="cursor: pointer; height: 22px;"
        @click="setMaxDate"
      >
        <v-icon x-small left color="primary">mdi-infinity</v-icon> Hiện tại (01/01/2100)
      </v-chip>

      <v-chip
        v-if="hasSuggest"
        x-small
        color="amber lighten-5"
        text-color="amber darken-4"
        class="font-weight-bold px-2"
        style="cursor: pointer; height: 22px;"
        @click="$emit('suggest')"
      >
        <v-icon x-small left color="amber darken-4">mdi-magic-staff</v-icon> {{ suggestText }}
      </v-chip>

      <v-chip
        v-if="hasMaternity"
        x-small
        color="purple lighten-5"
        text-color="purple darken-3"
        class="font-weight-bold px-2"
        style="cursor: pointer; height: 22px;"
        @click="setMaternityEndDate"
      >
        <v-icon x-small left color="purple darken-2">mdi-baby-carriage</v-icon> +6 tháng (Thai sản)
      </v-chip>
    </div>
  </div>
</template>

<script>
import moment from 'moment/moment';

export default {
  name: 'DateInput',
  props: {
    // Giá trị binding ngày định dạng DD/MM/YYYY
    value: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: 'dd/mm/yyyy'
    },
    rules: {
      type: Array,
      default: () => []
    },
    dense: {
      type: Boolean,
      default: true
    },
    outlined: {
      type: Boolean,
      default: true
    },
    flat: {
      type: Boolean,
      default: false
    },
    solo: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    clearable: {
      type: Boolean,
      default: true
    },
    hideDetails: {
      type: [Boolean, String],
      default: 'auto'
    },
    // Hiển thị phím tắt "Hiện tại / Vô thời hạn (01/01/2100)"
    isEndDate: {
      type: Boolean,
      default: false
    },
    // Nút gợi ý ngày tiếp theo từ API
    hasSuggest: {
      type: Boolean,
      default: false
    },
    suggestText: {
      type: String,
      default: 'Gợi ý tiếp theo'
    },
    // Phím tắt tính ngày nghỉ thai sản (+6 tháng)
    hasMaternity: {
      type: Boolean,
      default: false
    },
    fromDateVal: {
      type: String,
      default: ''
    },
    // Hiển thị thanh chip chọn nhanh bên dưới
    quickPresets: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      menu: false,
      displayValue: '',
      pickerDate: ''
    };
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        this.displayValue = newVal || '';
        if (newVal && moment(newVal, 'DD/MM/YYYY', true).isValid()) {
          this.pickerDate = moment(newVal, 'DD/MM/YYYY').format('YYYY-MM-DD');
        } else {
          this.pickerDate = '';
        }
      }
    }
  },
  methods: {
    // Mở popup lịch và cuộn đến ngày đang nhập (nếu hợp lệ) hoặc hôm nay
    openMenu() {
      if (this.disabled || this.readonly) return;
      if (this.displayValue && moment(this.displayValue, 'DD/MM/YYYY', true).isValid()) {
        this.pickerDate = moment(this.displayValue, 'DD/MM/YYYY').format('YYYY-MM-DD');
      } else {
        this.pickerDate = moment().format('YYYY-MM-DD');
      }
      this.menu = true;
    },

    // Xử lý khi chọn ngày trên popup lịch
    onPickerSelect(val) {
      if (!val) return;
      const formatted = moment(val, 'YYYY-MM-DD').format('DD/MM/YYYY');
      this.displayValue = formatted;
      this.$emit('input', formatted);
      this.$emit('change', formatted);
      this.menu = false;
    },

    // Xử lý khi người dùng gõ phím trực tiếp
    onTextInput(val) {
      this.displayValue = val || '';
      this.$emit('input', val || '');
      // Tự động đồng bộ với date picker nếu ngày nhập đã đủ 10 ký tự hợp lệ
      if (val && val.length === 10 && moment(val, 'DD/MM/YYYY', true).isValid()) {
        this.pickerDate = moment(val, 'DD/MM/YYYY').format('YYYY-MM-DD');
      }
    },

    // Xử lý sự kiện change khi hoàn tất nhập
    onTextChange(val) {
      this.$emit('change', val || '');
    },

    // Xóa ngày
    clearDate() {
      this.displayValue = '';
      this.pickerDate = '';
      this.$emit('input', '');
      this.$emit('change', '');
    },

    // Đặt ngày hôm nay
    setToday() {
      const today = moment().format('DD/MM/YYYY');
      this.displayValue = today;
      this.pickerDate = moment().format('YYYY-MM-DD');
      this.$emit('input', today);
      this.$emit('change', today);
      this.menu = false;
    },

    // Đặt ngày đầu tháng
    setFirstDayOfMonth() {
      const firstDay = moment().startOf('month').format('DD/MM/YYYY');
      this.displayValue = firstDay;
      this.pickerDate = moment().startOf('month').format('YYYY-MM-DD');
      this.$emit('input', firstDay);
      this.$emit('change', firstDay);
      this.menu = false;
    },

    // Đặt ngày kết thúc vô thời hạn (01/01/2100)
    setMaxDate() {
      const maxDate = '01/01/2100';
      this.displayValue = maxDate;
      this.pickerDate = '2100-01-01';
      this.$emit('input', maxDate);
      this.$emit('change', maxDate);
      this.menu = false;
    },

    // Đặt ngày kết thúc thai sản (+6 tháng từ ngày bắt đầu)
    setMaternityEndDate() {
      const base = this.fromDateVal && moment(this.fromDateVal, 'DD/MM/YYYY', true).isValid()
        ? moment(this.fromDateVal, 'DD/MM/YYYY')
        : moment();
      const matDate = base.clone().add(6, 'months').format('DD/MM/YYYY');
      this.displayValue = matDate;
      this.pickerDate = moment(matDate, 'DD/MM/YYYY').format('YYYY-MM-DD');
      this.$emit('input', matDate);
      this.$emit('change', matDate);
      this.menu = false;
    }
  }
};
</script>

<style scoped>
.date-input-container {
  width: 100%;
}
</style>
